import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, X, MapPin, Crosshair, Star, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ListingCard from '../components/ListingCard';
import { useAuth } from '../contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: '', name: 'Tutte le categorie' },
  { slug: 'ultraleggeri', name: 'Ultraleggeri' },
  { slug: 'propulsione-singola', name: 'Propulsione Singola' },
  { slug: 'propulsione-multipla', name: 'Propulsione Multipla' },
  { slug: 'turboelica', name: 'Turboelica' },
  { slug: 'jet', name: 'Jet' },
  { slug: 'elicotteri', name: 'Elicotteri' },
];

const mapContainerStyle = { width: '100%', height: '100%' };
// Centered on Europe (roughly central Europe — Germany/Austria area)
const defaultCenter = { lat: 50.5, lng: 10.5 };
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
};

export default function Listings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [favIds, setFavIds] = useState([]);
  const { user } = useAuth();

  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') || '');
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);
  const [distance, setDistance] = useState('');
  const [featuredOnly, setFeaturedOnly] = useState(searchParams.get('featured') === 'true');
  const [locationResults, setLocationResults] = useState([]);
  const [showLocResults, setShowLocResults] = useState(false);
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [locating, setLocating] = useState(false);
  const [mapScope, setMapScope] = useState('page'); // 'page' or 'all'
  const [allMapListings, setAllMapListings] = useState([]);
  const locTimerRef = useRef(null);

  const { isLoaded } = useGoogleMaps();

  const catLabelMap = { 'ultraleggeri': 'Ultraleggeri', 'propulsione-singola': 'Aerei Propulsione Singola', 'propulsione-multipla': 'Aerei Propulsione Multipla', 'turboelica': 'Turboelica', 'jet': 'Jet Privati', 'elicotteri': 'Elicotteri' };
  const catLabel = catLabelMap[category];
  useSEO({
    title: catLabel
      ? `${catLabel} in Vendita — Annunci Aeromobili`
      : 'Annunci Aeromobili in Vendita — Aerei, Ultraleggeri, Elicotteri',
    description: catLabel
      ? `Sfoglia tutti gli annunci di ${catLabel.toLowerCase()} in vendita su AeroDeals. Aeromobili verificati da venditori privati e operatori, con foto, ore motore, specifiche tecniche e contatto diretto.`
      : 'Sfoglia tutti gli annunci di aeromobili in vendita su AeroDeals: ultraleggeri, aerei a pistoni, turboelica, jet ed elicotteri. Filtra per categoria, prezzo, anno e posizione.',
    keywords: 'annunci aerei, aerei in vendita, ultraleggeri usati, elicotteri in vendita, jet privati usati, compravendita aeromobili',
  });

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort };
      if (category) params.category = category;
      if (search) params.search = search;
      if (featuredOnly) params.featured = 'true';
      if (locationLat && locationLng && distance) {
        params.near_lat = locationLat;
        params.near_lng = locationLng;
        params.near_radius = distance;
      }
      const res = await axios.get(`${API}/listings`, { params });
      setListings(res.data.listings);
      setTotal(res.data.total);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [category, search, sort, page, featuredOnly, locationLat, locationLng, distance]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/favorites/ids`, { withCredentials: true }).then(r => setFavIds(r.data)).catch(() => {});
    }
  }, [user]);

  const toggleFav = async (listingId) => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/favorites/${listingId}`, {}, { withCredentials: true });
      setFavIds(prev => res.data.favorited ? [...prev, listingId] : prev.filter(id => id !== listingId));
    } catch {}
  };

  useEffect(() => {
    const cat = searchParams.get('category');
    const q = searchParams.get('search');
    const loc = searchParams.get('location');
    const feat = searchParams.get('featured');
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const dist = searchParams.get('distance');
    if (cat !== null) setCategory(cat);
    if (q !== null) setSearch(q);
    if (loc !== null) setLocationFilter(loc);
    if (feat !== null) setFeaturedOnly(feat === 'true');
    if (lat !== null && lng !== null) {
      const flat = parseFloat(lat);
      const flng = parseFloat(lng);
      if (!Number.isNaN(flat) && !Number.isNaN(flng)) {
        setLocationLat(flat);
        setLocationLng(flng);
      }
    }
    if (dist !== null) setDistance(dist);
  }, [searchParams]);

  const searchLoc = (q) => {
    setLocationFilter(q);
    // Clear coords when user types (will be re-set on selection)
    setLocationLat(null);
    setLocationLng(null);
    if (locTimerRef.current) clearTimeout(locTimerRef.current);
    if (q.length < 2) { setLocationResults([]); setShowLocResults(false); return; }
    locTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/places/autocomplete`, { params: { q } });
        setLocationResults(res.data);
        setShowLocResults(true);
      } catch { setLocationResults([]); }
    }, 300);
  };

  const selectLocation = (r) => {
    setLocationFilter(r.description.split(',')[0]);
    setLocationLat(r.lat || null);
    setLocationLng(r.lng || null);
    setShowLocResults(false);
    setPage(1);
  };

  const clearLocation = () => {
    setLocationFilter('');
    setLocationLat(null);
    setLocationLng(null);
    setDistance('');
    setPage(1);
  };

  const saveAsAlert = async () => {
    if (!user) {
      toast.error('Accedi per creare un avviso sulle tue ricerche');
      setTimeout(() => { navigate('/login?redirect=/listings'); }, 900);
      return;
    }
    // Build readable name from current filters
    const parts = [];
    if (category) parts.push(CATEGORIES.find(c => c.slug === category)?.name || category);
    if (search) parts.push(`"${search}"`);
    if (locationFilter) parts.push(locationFilter);
    if (featuredOnly) parts.push('in evidenza');
    const defaultName = parts.length > 0 ? parts.join(' · ') : 'La mia ricerca';
    const name = window.prompt('Dai un nome a questo avviso\n\nRiceverai una notifica (e un\'email) ogni volta che un nuovo annuncio corrisponde ai filtri attuali.', defaultName);
    if (!name || !name.trim()) return;
    try {
      await axios.post(
        `${API}/alerts`,
        {
          name: name.trim(),
          category: category || null,
          manufacturer: search || null,
          location: locationFilter || null,
          email_notify: true,
        },
        { withCredentials: true }
      );
      toast.success('Avviso salvato! Gestiscilo da Dashboard → Avvisi');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel salvataggio avviso');
    }
  };

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setLocationLat(pos.coords.latitude);
        setLocationLng(pos.coords.longitude);
        try {
          const resp = await axios.get('https://nominatim.openstreetmap.org/reverse', {
            params: { lat: pos.coords.latitude, lon: pos.coords.longitude, format: 'json', 'accept-language': 'it' },
            headers: { 'User-Agent': 'AeroDeals/1.0' }
          });
          const city = resp.data?.address?.city || resp.data?.address?.town || resp.data?.address?.village || '';
          setLocationFilter(city || `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
        } catch {
          setLocationFilter(`${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
        }
        setPage(1);
        setLocating(false);
      },
      () => { setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchParams(prev => {
      if (search) prev.set('search', search);
      else prev.delete('search');
      return prev;
    });
    fetchListings();
  };

  // Fetch ALL listings (up to 500) for map "all" scope — only when map is open and scope=all
  useEffect(() => {
    if (!showMap || mapScope !== 'all') return;
    const params = { limit: 500, sort };
    if (category) params.category = category;
    if (search) params.search = search;
    if (featuredOnly) params.featured = 'true';
    if (locationLat && locationLng && distance) {
      params.near_lat = locationLat;
      params.near_lng = locationLng;
      params.near_radius = distance;
    }
    axios.get(`${API}/listings`, { params })
      .then(r => setAllMapListings(r.data.listings || []))
      .catch(() => setAllMapListings([]));
  }, [showMap, mapScope, category, search, featuredOnly, sort, locationLat, locationLng, distance]);

  const markerSource = showMap && mapScope === 'all' ? allMapListings : listings;
  const markers = markerSource.filter(l => l.location_lat && l.location_lng).map(l => ({
    id: l.listing_id,
    position: { lat: l.location_lat, lng: l.location_lng },
    title: l.title,
    listing: l,
  }));

  const [selectedMarker, setSelectedMarker] = useState(null);

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="listings-page">
      {/* Header */}
      <div className="border-b border-zinc-800/50 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-2">Marketplace</p>
              <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {category ? CATEGORIES.find(c => c.slug === category)?.name || 'Annunci' : 'Tutti gli Annunci'}
              </h1>
              <p className="text-sm text-zinc-500 mt-1">{total} risultati</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={saveAsAlert}
                className="flex items-center gap-2 px-4 py-2.5 text-sm border border-zinc-700 text-zinc-400 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors"
                title="Ricevi notifiche quando nuovi annunci corrispondono a questi filtri"
                data-testid="save-search-btn"
              >
                <Bell size={14} /> Salva ricerca
              </button>
              <button
                onClick={() => setShowMap(!showMap)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors ${showMap ? 'border-[#C5A059] text-[#C5A059]' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                data-testid="toggle-map-btn"
              >
                <MapPin size={14} /> Mappa
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm border border-zinc-700 text-zinc-400 hover:border-zinc-500 transition-colors md:hidden"
                data-testid="toggle-filters-btn"
              >
                <SlidersHorizontal size={14} /> Filtri
              </button>
            </div>
          </div>

          {/* Filters Bar */}
          <div className={`mt-6 flex flex-wrap gap-3 items-center ${showFilters ? '' : 'hidden md:flex'}`}>
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <Input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Cerca velivoli..."
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white h-10 placeholder-zinc-600"
                  data-testid="search-input"
                />
              </div>
            </form>

            {/* Location + Distance filter (combined) */}
            <div className="relative flex items-stretch border border-zinc-800 bg-zinc-900 focus-within:border-[#C5A059] transition-colors" data-testid="location-distance-wrapper">
              <div className="relative flex-1 min-w-[170px]">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
                <input
                  type="text"
                  value={locationFilter}
                  onChange={e => searchLoc(e.target.value)}
                  onFocus={() => locationResults.length > 0 && setShowLocResults(true)}
                  onBlur={() => setTimeout(() => setShowLocResults(false), 200)}
                  placeholder="Posizione..."
                  className="h-10 w-full pl-9 pr-16 bg-transparent text-white text-sm placeholder-zinc-600 outline-none"
                  data-testid="location-filter"
                />
                <button
                  type="button"
                  onClick={handleLocate}
                  disabled={locating}
                  className="absolute right-8 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#C5A059] hover:text-[#D4B271] transition-colors disabled:opacity-50"
                  title="Localizzami"
                  aria-label="Localizzami"
                  data-testid="locate-me-btn"
                >
                  <Crosshair size={14} className={locating ? 'animate-spin' : ''} />
                </button>
                {locationFilter && (
                  <button
                    type="button"
                    onClick={clearLocation}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-zinc-500 hover:text-white"
                    data-testid="location-clear"
                    aria-label="Cancella posizione"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <div className="w-px bg-zinc-800" />
              <select
                value={distance}
                onChange={e => { setDistance(e.target.value); setPage(1); }}
                disabled={!locationLat || !locationLng}
                className="h-10 bg-transparent text-sm text-zinc-300 px-3 pr-8 outline-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed appearance-none"
                data-testid="distance-filter"
                title={(!locationLat || !locationLng) ? 'Seleziona prima una posizione dai suggerimenti' : 'Raggio di ricerca'}
              >
                <option value="" className="bg-zinc-900">Distanza</option>
                <option value="25" className="bg-zinc-900">+ 25 km</option>
                <option value="50" className="bg-zinc-900">+ 50 km</option>
                <option value="100" className="bg-zinc-900">+ 100 km</option>
                <option value="250" className="bg-zinc-900">+ 250 km</option>
                <option value="500" className="bg-zinc-900">+ 500 km</option>
              </select>
              {showLocResults && locationResults.length > 0 && (
                <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-zinc-900 border border-zinc-700 max-h-48 overflow-auto">
                  {locationResults.map((r, i) => (
                    <button key={i} type="button" onMouseDown={() => selectLocation(r)} className="block w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white border-b border-zinc-800 last:border-0">
                      <MapPin size={10} className="inline mr-1.5 text-[#C5A059]" />{r.description}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Select value={category} onValueChange={v => { setCategory(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-800 text-zinc-300 h-10" data-testid="category-filter">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                <SelectItem value="all" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">Tutte</SelectItem>
                {CATEGORIES.filter(c => c.slug).map(c => (
                  <SelectItem key={c.slug} value={c.slug} className="text-zinc-300 focus:bg-zinc-800 focus:text-white">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-zinc-300 h-10" data-testid="sort-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                <SelectItem value="newest" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">Più recenti</SelectItem>
                <SelectItem value="price_asc" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">Prezzo crescente</SelectItem>
                <SelectItem value="price_desc" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">Prezzo decrescente</SelectItem>
                <SelectItem value="year" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">Anno</SelectItem>
              </SelectContent>
            </Select>

            {/* Featured toggle */}
            <button
              onClick={() => {
                const newVal = !featuredOnly;
                setFeaturedOnly(newVal);
                setSearchParams(prev => { if (newVal) prev.set('featured', 'true'); else prev.delete('featured'); return prev; });
              }}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 border transition-colors ${featuredOnly ? 'border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10' : 'border-zinc-800 text-zinc-500 hover:text-zinc-300'}`}
              data-testid="featured-filter-btn"
            >
              <Star size={12} /> In Evidenza
            </button>

            {(category || search || locationFilter || featuredOnly) && (
              <button
                onClick={() => { setCategory(''); setSearch(''); setLocationFilter(''); setFeaturedOnly(false); setSearchParams({}); }}
                className="flex items-center gap-1.5 text-xs px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 transition-colors"
                data-testid="clear-filters-btn"
              >
                <X size={12} /> Azzera filtri
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={showMap ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : ''}>
          {/* Map */}
          {showMap && isLoaded && (
            <div className="lg:sticky lg:top-24 flex flex-col gap-2 h-[500px] lg:h-[calc(100vh-280px)]" data-testid="listings-map-wrapper">
              {/* Map scope toggle */}
              <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 p-1 text-[11px] font-mono uppercase tracking-wider" data-testid="map-scope-toggle">
                <button
                  type="button"
                  onClick={() => setMapScope('page')}
                  className={`flex-1 h-8 transition-colors ${mapScope === 'page' ? 'bg-[#C5A059] text-black' : 'text-zinc-400 hover:text-white'}`}
                  data-testid="map-scope-page"
                >
                  Pagina corrente ({listings.length})
                </button>
                <button
                  type="button"
                  onClick={() => setMapScope('all')}
                  className={`flex-1 h-8 transition-colors ${mapScope === 'all' ? 'bg-[#C5A059] text-black' : 'text-zinc-400 hover:text-white'}`}
                  data-testid="map-scope-all"
                >
                  Tutti gli annunci ({mapScope === 'all' ? allMapListings.length : total})
                </button>
              </div>
              <div className="flex-1 border border-zinc-800 overflow-hidden" data-testid="listings-map">
                <GoogleMap mapContainerStyle={mapContainerStyle} center={defaultCenter} zoom={4} options={mapOptions} onClick={() => setSelectedMarker(null)}>
                  {markers.map(m => (
                    <Marker key={m.id} position={m.position} title={m.title} onClick={() => setSelectedMarker(m)} />
                  ))}
                  {selectedMarker && (
                    <InfoWindow position={selectedMarker.position} onCloseClick={() => setSelectedMarker(null)}>
                      <div style={{ maxWidth: '220px', fontFamily: 'system-ui, sans-serif' }}>
                        {selectedMarker.listing.images?.length > 0 ? (
                          <img src={`${API}/files/${selectedMarker.listing.images[0]}`} alt={selectedMarker.title} style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px' }} />
                        ) : (
                          <div style={{ width: '100%', height: '80px', background: '#27272a', borderRadius: '4px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#71717a', fontSize: '11px' }}>Nessuna foto</div>
                        )}
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#18181b', marginBottom: '2px' }}>{selectedMarker.title}</div>
                        <div style={{ fontSize: '11px', color: '#71717a', marginBottom: '4px' }}>{selectedMarker.listing.location_city}</div>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#b8933f', marginBottom: '8px' }}>
                          {selectedMarker.listing.price ? `€${Number(selectedMarker.listing.price).toLocaleString('it-IT', { maximumFractionDigits: 0 })}` : 'Prezzo su richiesta'}
                        </div>
                        <button onClick={() => navigate(selectedMarker.listing.slug ? `/listings/${selectedMarker.listing.slug}` : `/listings/${selectedMarker.id}`)} style={{ display: 'block', width: '100%', textAlign: 'center', background: '#18181b', color: '#C5A059', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' }}>Vai all'annuncio</button>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </div>
            </div>
          )}

          {/* Listings Grid */}
          <div>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 animate-pulse">
                    <div className="aspect-[4/3] bg-zinc-800" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-zinc-800 rounded w-3/4" />
                      <div className="h-3 bg-zinc-800 rounded w-1/2" />
                      <div className="h-5 bg-zinc-800 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : listings.length > 0 ? (
              <>
                <div className={`grid gap-6 ${showMap ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                  {listings.map(listing => (
                    <ListingCard key={listing.listing_id} listing={listing} isFavorited={favIds.includes(listing.listing_id)} onToggleFavorite={user ? toggleFav : undefined} />
                  ))}
                </div>
                {/* Pagination */}
                {total > 12 && (
                  <div className="flex items-center justify-center gap-2 mt-12" data-testid="listings-pagination">
                    <button
                      onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      disabled={page === 1}
                      className="flex items-center gap-1.5 px-4 h-10 border border-zinc-800 text-zinc-400 text-xs uppercase tracking-wider hover:border-[#C5A059]/50 hover:text-[#C5A059] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-zinc-800 disabled:hover:text-zinc-400"
                      data-testid="pagination-prev"
                    >
                      ‹ Precedente
                    </button>
                    {(() => {
                      const totalPages = Math.ceil(total / 12);
                      const pages = [];
                      const addPage = (n) => pages.push(
                        <button
                          key={n}
                          onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          className={`w-10 h-10 border text-xs transition-colors ${page === n ? 'border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white'}`}
                          data-testid={`pagination-page-${n}`}
                        >
                          {n}
                        </button>
                      );
                      const addEllipsis = (key) => pages.push(<span key={key} className="w-10 h-10 flex items-center justify-center text-zinc-600 text-xs">…</span>);
                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) addPage(i);
                      } else {
                        addPage(1);
                        if (page > 3) addEllipsis('l');
                        const start = Math.max(2, page - 1);
                        const end = Math.min(totalPages - 1, page + 1);
                        for (let i = start; i <= end; i++) addPage(i);
                        if (page < totalPages - 2) addEllipsis('r');
                        addPage(totalPages);
                      }
                      return pages;
                    })()}
                    <button
                      onClick={() => { setPage(p => Math.min(Math.ceil(total / 12), p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      disabled={page >= Math.ceil(total / 12)}
                      className="flex items-center gap-1.5 px-4 h-10 border border-zinc-800 text-zinc-400 text-xs uppercase tracking-wider hover:border-[#C5A059]/50 hover:text-[#C5A059] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-zinc-800 disabled:hover:text-zinc-400"
                      data-testid="pagination-next"
                    >
                      Successiva ›
                    </button>
                  </div>
                )}
                {total > 12 && (
                  <p className="text-center text-[11px] font-mono text-zinc-600 uppercase tracking-wider mt-4" data-testid="pagination-info">
                    Pagina {page} di {Math.ceil(total / 12)} — {total} annunci totali
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-20" data-testid="no-listings-message">
                <p className="text-zinc-500 text-base mb-4">Nessun annuncio trovato</p>
                <Link to="/create-listing" className="text-[#C5A059] text-sm hover:underline">
                  Pubblica il primo annuncio
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
